'use client';

import type { ReactNode } from 'react';
import Markdown from '@/app/common/Markdown';
import { FootnoteDefinition } from '@/lib/parseMarkdown';
import type { Appendix } from './detailsToAppendices';
import { makePrintSidenote } from './printSidenote';

/**
 * Renders the back-matter Appendices collected from a document's expandable
 * `:::details` boxes (see detailsToAppendices). Used both for a standalone
 * supplement (PrintEssay) and per-document inside the full book (PrintScenario)
 * so each set of appendices sits right after the text that cross-references it.
 *
 * `heading` defaults to "Appendices"; pass a scoped title (e.g. a supplement
 * name) when several appendix sets share one book. Pass `divider` (a
 * `.print-part-divider` page) to open the set with a full-page divider
 * instead of the compact heading — the book does this for the main
 * scenario's appendices.
 */
export default function Appendices({
  appendices,
  footnotes,
  heading = 'Appendices',
  divider,
}: {
  appendices: Appendix[];
  footnotes: FootnoteDefinition[];
  heading?: string;
  divider?: ReactNode;
}) {
  if (appendices.length === 0) return null;
  const Sidenote = makePrintSidenote(footnotes);
  return (
    <>
      {divider}
      <section className="print-appendices">
        {divider ? null : <h1>{heading}</h1>}
        {appendices.map((appendix) => (
          <div key={appendix.id}>
            <h2 className="print-appendix-title" id={appendix.id}>
              Appendix {appendix.letter} — {appendix.title}
            </h2>
            <div className="markdown-content">
              <Markdown nodes={appendix.children} FootnoteHandler={Sidenote} />
            </div>
          </div>
        ))}
      </section>
    </>
  );
}
